import { supportedLocales } from "@/i18n";
import {
  AdvancedSearchFieldConfig,
  SearchOperator,
  StringFieldSearchConfig,
  SubmitableSortField,
  SubmittableSearchFormField,
} from "./advanced-search-fields-def";

/** Marker for attachment photo metadata filters (resolved server-side via docId). */
export const ATTACHMENT_PHOTO_SEARCH_MARKER = "__attachmentPhotoSearch";

const ATTACHMENT_PHOTO_FIELD_KEYS = [
  "attachment.photoCredits",
  "attachment.notes",
] as const;

/** Key used in config for "Collection actuelle" enum (optionsFrom: "collectors"). When building the where clause we target the relation ID. */
const COLLECTION_ENUM_KEY =
  "oeuvresLocalisationHistoriqueCollection.collectionneursDescriptionNom";
const COLLECTION_WHERE_KEY = "oeuvresLocalisationHistoriqueCollectionId";

/** Key for "ensemble temporaire" filter: filter by relation relatedEnsemble (some/none). */
const RELATED_ENSEMBLE_KEY = "relatedEnsemble";

/**
 * Applies the prisma where creation logic to a JSON.
 *
 * There is one major difference with the other data types:
 * Since the mapComparator function doesn't add the key to the created where clause
 * and is applied as a map, we are forced to return an OR clause as a single item,
 * which will later be skipped by the keyToNestedObject function.
 * @param paths The list of paths to apply the comparator to. Each path must be formatted
 * as an array of strings, thus matching with the way prisma handles json paths.
 * @param comparator
 * @param key
 * @param value
 * @returns An array of partial where clauses
 */
function mapJsonToArray(
  paths: string[][],
  comparator: SubmittableSearchFormField["comparator"],
  key: string,
  value: unknown,
) {
  let prismaComparator: string;
  switch (comparator) {
    case "contains":
      prismaComparator = "string_contains";
      break;
    // add more comparators here if needed
    default:
      throw new Error("Invalid comparator");
  }

  if (paths.length === 0) {
    throw new Error("No paths provided");
  }

  const whereClause = {
    OR: paths.map((path) =>
      keyToNestedObject(key, {
        path: path,
        [prismaComparator]: value,
        mode: "insensitive",
      }),
    ),
  };
  return whereClause;
}

export function mapComparator(
  type: SubmittableSearchFormField["type"],
  comparator: SubmittableSearchFormField["comparator"],
  key: string,
  value: any,
): object {
  if (type === "currency") {
    return { path: ["amount"], [comparator]: value * 100 };
  }

  if (type === "multilingual") {
    const paths = supportedLocales.map((locale) => [locale]);
    return mapJsonToArray(paths, comparator, key, value);
  }

  // Enum with optionsFrom "collectors" → filter by relation ID (Prisma rejects mode on ID fields)
  if (
    key === COLLECTION_WHERE_KEY ||
    key === "collectorId" ||
    key.endsWith(".collectorId")
  ) {
    switch (comparator) {
      case "equals":
        return { equals: value };
      case "notEquals":
        return { not: { equals: value } };
      default:
        return { [comparator]: value };
    }
  }

  switch (comparator) {
    case "contains":
    case "equals":
    case "startsWith":
    case "endsWith":
      return {
        [comparator]: value,
        ...(type === "string" ? { mode: "insensitive" } : {}),
      };
    case "empty":
      return { equals: "" };
    case "notEquals":
      return {
        not: {
          equals: value,
          ...(type === "string" ? { mode: "insensitive" } : {}),
        },
      };
    case "notContains":
      return { not: { contains: value } };
    case "notEmpty":
      return { not: { equals: "" } };
    default:
      return { [comparator]: value };
  }
}

export function keyToNestedObject(key: string, value: unknown): object {
  if (
    typeof value === "object" &&
    value != null &&
    ["OR"].includes(Object.keys(value)[0])
  ) {
    return { ...value };
  }

  return key
    .split(".")
    .reverse()
    .reduce((acc, curr) => ({ [curr]: acc }), value) as object;
}

/**
 *
 * @param f
 * @returns undefined when the data is not coherent.
 *          This includes :
 *              - When field "value" is wrongly undefined
 */
function getValueKeyDependingOnType(f: SubmittableSearchFormField):
  | {
      value: unknown;
      key: string;
    }
  | undefined {
  switch (f.type) {
    case "number":
      return {
        value:
          Number.parseFloat(f.value?.toString() ?? "0") * (f.multiplyBy ?? 1),
        key: f.key,
      };
    case "enum": {
      if (f.useBooleans === true) {
        if (f.value == undefined) return undefined;
        return { key: f.value, value: true };
      }
      // Enum with optionsFrom "collectors": value is collection id, filter by relation id
      if (f.optionsFrom === "collectors" && f.value != null && f.value !== "") {
        if (f.key === COLLECTION_ENUM_KEY) {
          return { key: COLLECTION_WHERE_KEY, value: f.value };
        }
        const collectorIdKey = f.key.replace(
          /\.collector\.collectionneursDescriptionNom$/,
          ".collectorId",
        );
        if (collectorIdKey !== f.key) {
          return { key: collectorIdKey, value: f.value };
        }
      }
      return { key: f.key, value: f.value };
    }
    default:
      return { key: f.key, value: f.value };
  }
}

/**
 *
 * @param f
 * @returns undefined when the data is not coherent
 */
export function advancedSearchFieldToPrismaWhere(
  f: SubmittableSearchFormField,
): object | undefined {
  if (
    ATTACHMENT_PHOTO_FIELD_KEYS.includes(
      f.key as (typeof ATTACHMENT_PHOTO_FIELD_KEYS)[number],
    )
  ) {
    const subKey =
      f.key === "attachment.photoCredits" ? "photoCredits" : "notes";
    const valueRequired = !["empty", "notEmpty"].includes(f.comparator ?? "");
    if (valueRequired && (f.value == null || f.value === "")) return undefined;
    return {
      [ATTACHMENT_PHOTO_SEARCH_MARKER]: {
        subKey,
        comparator: f.comparator,
        ...(valueRequired ? { value: f.value } : {}),
      },
    };
  }

  // "Fait partie" / "Ne fait pas partie" d'un ensemble temporaire: filter by relatedEnsemble relation
  if (f.type === "temporarySet") {
    const comparator = f.comparator;
    if (comparator === "inAnySet") {
      return keyToNestedObject(RELATED_ENSEMBLE_KEY, { some: {} });
    }
    if (comparator === "notInAnySet") {
      return keyToNestedObject(RELATED_ENSEMBLE_KEY, { none: {} });
    }
    const ensembleId = f.value;
    if (ensembleId == null || ensembleId === "") return undefined;
    const relationFilter =
      comparator === "inSet"
        ? { some: { id: ensembleId } }
        : { none: { id: ensembleId } };
    return keyToNestedObject(RELATED_ENSEMBLE_KEY, relationFilter);
  }

  const vk = getValueKeyDependingOnType(f);

  if (!vk) return undefined;

  const baseQuery = keyToNestedObject(
    vk.key.split(".").slice(-1)[0], // Get the last part of the key
    mapComparator(f.type, f.comparator ?? "equals", vk.key, vk.value),
  );

  // If there's no dot in the key, or no additional query, return simple query
  if (!vk.key.includes(".") || !f.additionalQuery?.length) {
    return keyToNestedObject(
      vk.key,
      mapComparator(f.type, f.comparator ?? "equals", vk.key, vk.value),
    );
  }

  // Get the parent key (everything before the last dot)
  const parentKey = vk.key.split(".").slice(0, -1).join(".");

  // Combine base query with additional queries under the parent key
  return keyToNestedObject(parentKey, {
    AND: [baseQuery, ...f.additionalQuery],
  });
}

export function advancedSearchToPrismaWhereContent(
  f: SubmittableSearchFormField[],
  mode: SearchOperator,
): object {
  const mappedFields = f
    .map(advancedSearchFieldToPrismaWhere)
    .filter((field) => field != undefined);

  if (mappedFields.length == 0) return {};
  if (mappedFields.length == 1) return mappedFields[0];

  return {
    [mode.toLocaleUpperCase()]: mappedFields,
  };
}

function sortKeyToPrismaPath(key: string) {
  return key.replace(/\.(some|none)\./g, ".");
}

export function sortFieldsToPrismaOrderByContent(
  fields: SubmitableSortField[],
) {
  return fields.map((item) =>
    keyToNestedObject(sortKeyToPrismaPath(item.key), item.sortMode),
  );
}
export function globalSearchToPrismaWhereContent(
  flatAndFilteredConfig: AdvancedSearchFieldConfig<StringFieldSearchConfig>[],
  searchContent: string,
) {
  const orContent = flatAndFilteredConfig.map((c) => ({
    [c.key]: { contains: searchContent, mode: "insensitive" },
  }));

  return orContent.length > 0 ? { OR: orContent } : {};
}
